import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Mail, 
  FileText, 
  Users, 
  BarChart3, 
  BookOpen, 
  CheckCircle2, 
  Shield, 
  Bell,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Database,
  Clock
} from 'lucide-react';
import logoImage from '../images/image.png';
import pLogoImage from '../images/p_logo.jpg';
import dashboardImage from '../images/Real-time Dashboard.png';
import newPublicationImage from '../images/new_publication.png';
import joinCoauthor1Image from '../images/Join-coauthor1.png';
import joinCoauthor2Image from '../images/join-coauthor2.png';
import meetingMgn1Image from '../images/meeting_mgn1.png';
import meetingMgn2Image from '../images/meeting_mgn2.png';
import addTimelineImage from '../images/add_timeline.png';
import trackHistoryImage from '../images/track_history.png';
import projectObjImage from '../images/project_obj_with_attachments.png';
import emailNotifyImage from '../images/email_notify.png';
import postJournalImage from '../images/post_journal.png';
import PubMedInt from '../images/pubMed.png';
// Data Finder System images
import dataFinderDashboardImage from '../images/Dashboard.png';
import searchFilterImage from '../images/Search_Filter.png';
import cardViewImage from '../images/Card_View.png';
import listViewImage from '../images/List_View.png';
import datasetDetailImage from '../images/Detail.png';
import editHistoryImage from '../images/Edit_History.png';
import updatePanelImage from '../images/Update_Panel.png';

const Login: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [showMoreFeatures, setShowMoreFeatures] = useState(false);
  const [activeGuide, setActiveGuide] = useState<'publication' | 'datafinder'>('publication');

  const handleLogin = async () => {
    try {
      await login();
    } catch (error: any) {
      console.error('Login error:', error);
    }
  };

  const features = [
    {
      icon: FileText,
      title: 'Publication Submission',
      description: 'Submit analysis plans with abstracts, methods, and expected results'
    },
    {
      icon: Users,
      title: 'Co-author Recruitment',
      description: 'Request to join publications and collaborate with research teams'
    },
    {
      icon: BarChart3,
      title: 'Stage Management',
      description: 'Track publications through the 4-stage pipeline from ideation to publication'
    },
    {
      icon: Bell,
      title: 'Email Notifications',
      description: 'Receive automated alerts for submissions, approvals, and requests'
    },
    {
      icon: BookOpen,
      title: 'Post-Journal Approval Updates',
      description: 'After journal approval, update the formal title, abstract, and upload datasets, code, etc'
    }
  ];

  const stages = [
    {
      number: '1',
      name: 'Ideation',
      description: 'Submit analysis plans with abstracts, methods, and expected results',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      number: '2',
      name: 'Review & Team',
      description: 'Committee approval and co-author recruitment',
      color: 'bg-yellow-100 text-yellow-700'
    },
    {
      number: '3',
      name: 'Data Analysis',
      description: 'Analysis execution with Data Team support',
      color: 'bg-purple-100 text-purple-700'
    },
    {
      number: '4',
      name: 'Manuscript',
      description: 'Drafting and refining for submission',
      color: 'bg-green-100 text-green-700'
    }
  ];

  const roles = [
    {
      role: 'Superadmin',
      description: 'Full system access, user role management',
      features: ['User management', 'System configuration', 'All admin features']
    },
    {
      role: 'Admin (Publication Committee)',
      description: 'Publication approval/rejection, stage management',
      features: ['Review submissions', 'Approve/reject publications', 'Manage stages']
    },
    {
      role: 'User',
      description: 'Publication submission, co-author requests',
      features: ['Submit publications', 'Request to join as co-author', 'Track your publications']
    }
  ];

  return (
    <div className={`min-h-screen ${activeGuide === 'publication' ? 'bg-gradient-to-br from-green-50 via-blue-50 to-purple-50' : 'bg-gradient-to-br from-purple-50 via-blue-50 to-slate-50'}`}>
      {/* Hero Section */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-6 mb-6">
              <img
                src={logoImage}
                alt="Publication System Logo"
                style={{ height: '90px', width: 'auto', objectFit: 'contain' }}
              />
              <img
                src={pLogoImage}
                alt="Publication Logo"
                style={{ height: '150px', width: 'auto', objectFit: 'contain' }}
              />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              {activeGuide === 'publication' 
                ? 'Publication Committee & Tracking System'
                : 'Research Data Inventory System'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {activeGuide === 'publication'
                ? 'Streamline collaboration and track your research from idea to publication'
                : 'Discover and manage research datasets across Savic Lab'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" aria-label="User Guides">
            <button
              onClick={() => setActiveGuide('publication')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeGuide === 'publication'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Publication System
            </button>
            <button
              onClick={() => setActiveGuide('datafinder')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeGuide === 'datafinder'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Data Finder System
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - System Guide */}
          <div className="lg:col-span-2 space-y-8">
            {activeGuide === 'publication' ? (
              <>
            {/* 4-Stage Pipeline */}
            <section className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <BarChart3 className="h-6 w-6 mr-2 text-green-600" />
                Publication Pipeline
              </h2>
              <p className="text-gray-600 mb-6">
                Our system manages publications through a structured 4-stage process:
              </p>
              
              {/* Stage Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {stages.map((stage) => (
                  <div
                    key={stage.number}
                    className="border-2 border-gray-200 rounded-lg p-4 hover:border-green-500 transition-colors"
                  >
                    <div className="flex items-start">
                      <div className={`${stage.color} rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0`}>
                        {stage.number}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">
                          Stage {stage.number}: {stage.name}
                        </h3>
                        <p className="text-sm text-gray-600">{stage.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* How It Works */}
              <div className="border-t pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <ArrowRight className="h-5 w-5 mr-2 text-green-600" />
                  How It Works
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full w-8 h-8 flex items-center justify-center font-bold text-green-700 mr-4 flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Submit Publication</h4>
                      <p className="text-sm text-gray-600">
                        Researchers submit analysis plans with all required details
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full w-8 h-8 flex items-center justify-center font-bold text-green-700 mr-4 flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Committee Review</h4>
                      <p className="text-sm text-gray-600">
                        Publication Committee reviews and approves or requests revisions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full w-8 h-8 flex items-center justify-center font-bold text-green-700 mr-4 flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Co-author Recruitment</h4>
                      <p className="text-sm text-gray-600">
                        Researchers can request to join approved publications as co-authors
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full w-8 h-8 flex items-center justify-center font-bold text-green-700 mr-4 flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Track Progress</h4>
                      <p className="text-sm text-gray-600">
                        Monitor publications through all stages with complete audit trails
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Real-Time Dashboard */}
            <section className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <BarChart3 className="h-6 w-6 mr-2 text-green-600" />
                Real-Time Dashboard
              </h2>
              <p className="text-gray-600 mb-4">
                Get instant insights into your publication pipeline with live statistics and tracking
              </p>
              <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md">
                <img
                  src={dashboardImage}
                  alt="Real-Time Dashboard showing publication statistics"
                  className="w-full h-auto"
                />
              </div>
              <p className="text-sm text-gray-500 mt-3 text-center">
                Track total papers, in-progress publications, and published works at a glance
              </p>
            </section>

            {/* Key Features */}
            <section className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <CheckCircle2 className="h-6 w-6 mr-2 text-green-600" />
                Key Features
              </h2>
              
              {/* PubMed Integration Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <Database className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">PubMed Integration</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Automatically sync and track grant-funded publications from PubMed. View publications by grant number, refresh data from PubMed, and access detailed publication information including citations, DOI, and abstracts.
                    </p>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={PubMedInt}
                        alt="PubMed Integration - Grant Publications Tracking"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Publication Submission Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <FileText className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Publication Submission</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Submit comprehensive analysis plans with abstracts, methods, and expected results
                    </p>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={newPublicationImage}
                        alt="New Publication Submission Form"
                        className="w-[60%] h-auto mx-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Co-author Recruitment Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Co-author Recruitment</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Request to join publications and collaborate with research teams
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                        <img
                          src={joinCoauthor1Image}
                          alt="Co-author Request Interface"
                          className="w-full h-auto"
                        />
                      </div>
                      <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                        <img
                          src={joinCoauthor2Image}
                          alt="Co-author Request Details"
                          className="w-full h-auto"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline Management Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <BarChart3 className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Timeline Management</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Create and manage timelines for your publications with key milestones
                    </p>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={addTimelineImage}
                        alt="Add Timeline Interface"
                        className="w-[80%] h-auto mx-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Meeting Management Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Meeting Management</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Schedule and manage meetings related to your publications. Attach files and add meeting notes to keep track of important discussions and decisions.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                        <img
                          src={meetingMgn1Image}
                          alt="Meeting Management Interface"
                          className="w-full h-auto"
                        />
                      </div>
                      <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                        <img
                          src={meetingMgn2Image}
                          alt="Meeting Details with File Attachments and Notes"
                          className="w-full h-auto"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Objectives with Attachments Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <FileText className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Project Objectives with Attachments</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Define and track project objectives with the ability to attach supporting documents, files, and resources
                    </p>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={projectObjImage}
                        alt="Project Objectives with File Attachments"
                        className="w-[80%] h-auto mx-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Track History and Version Control Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <Clock className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Track History and Version Control</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      View complete edit history and track all changes made to publications, including who made changes and when
                    </p>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={trackHistoryImage}
                        alt="Track History and Version Control - Edit History Interface"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Notifications Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <Bell className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Email Notifications</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Receive automated email alerts for submissions, approvals, co-author requests, stage changes, and other important updates
                    </p>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={emailNotifyImage}
                        alt="Email Notification System"
                        className="w-[60%] h-auto mx-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Post-Journal Approval Updates Feature */}
              <div className="mb-8">
                <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all mb-4">
                  <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                    <BookOpen className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Post-Journal Approval Updates</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      After journal approval, update the formal title, abstract, and upload datasets, code, and other supporting materials
                    </p>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={postJournalImage}
                        alt="Post-Journal Approval Updates - Title, Abstract, and File Uploads"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Other Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {features.filter(f => 
                  !['Publication Submission', 'Co-author Recruitment', 'Stage Management', 'Email Notifications', 'Post-Journal Approval Updates'].includes(f.title)
                ).map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all"
                    >
                      <div className="bg-green-100 rounded-lg p-2 mr-4 flex-shrink-0">
                        <Icon className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
              </>
            ) : (
              <>
                {/* Data Finder System Guide */}
                {/* System Overview */}
                <section className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <CheckCircle2 className="h-8 w-8 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">System Overview</h2>
                  </div>
                  <p className="text-gray-600 mb-4">
                    The Research Data Inventory System is a catalog that helps you discover and locate research datasets. This system displays metadata and file paths. To access files, access them via 'Data Request Management System' and your own User-based Dashboard based on different type and security level of files.
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-4">
                      <Database className="h-6 w-6 text-purple-600 mt-1 flex-shrink-0" />
                      <div>
                        <strong className="text-gray-900 block mb-1">Inventory System</strong>
                        <span className="text-gray-500 text-sm">This is a catalog/index system for discovering and locating research datasets. Files remain in their original locations.</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <FileText className="h-6 w-6 text-purple-600 mt-1 flex-shrink-0" />
                      <div>
                        <strong className="text-gray-900 block mb-1">Filepath Display</strong>
                        <span className="text-gray-500 text-sm">The system shows where files are stored. Use the filepath to access files via Data Request Management System or User-based Dashboard.</span>
                      </div>
                    </li>
                  </ul>
                </section>

                {/* Dashboard Overview */}
                <section className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <BarChart3 className="h-8 w-8 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
                  </div>
                  <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md">
                    <img
                      src={dataFinderDashboardImage}
                      alt="Data Finder Dashboard Overview"
                      className="w-full h-auto"
                    />
                  </div>
                </section>

                {/* Search and Filter */}
                <section className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <Database className="h-8 w-8 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">How to Search and Filter Datasets</h2>
                  </div>
                  <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md mb-4">
                    <img
                      src={searchFilterImage}
                      alt="Search and Filter Interface"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="text-gray-600 mb-4">
                    Use the search bar to find datasets by file name or description. You can also use filters to narrow down results by Data Type, Source, Medication, Added By, Date From, and Sort By options. Combine multiple filters for more precise searches.
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800 text-sm">
                      <strong>💡 Tip:</strong> Use the "Reset Filters" button to clear all filters at once.
                    </p>
                  </div>
                </section>

                {/* View Modes */}
                <section className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <BarChart3 className="h-8 w-8 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">View Modes</h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Toggle between Card View and List View to display datasets in your preferred format. Card View shows key information in an easy-to-scan card layout, while List View provides a tabular format with sortable columns for quick comparison.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md mb-2">
                        <img
                          src={cardViewImage}
                          alt="Card View"
                          className="w-full h-auto"
                        />
                      </div>
                      <p className="text-sm text-gray-500 italic">Card View</p>
                    </div>
                    <div className="text-center">
                      <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md mb-2">
                        <img
                          src={listViewImage}
                          alt="List View"
                          className="w-full h-auto"
                        />
                      </div>
                      <p className="text-sm text-gray-500 italic">List View</p>
                    </div>
                  </div>
                </section>

                {/* Viewing Dataset Details */}
                <section className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <CheckCircle2 className="h-8 w-8 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">Viewing Dataset Details</h2>
                  </div>
                  <div className="flex justify-center mb-4">
                    <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md w-3/4">
                      <img
                        src={datasetDetailImage}
                        alt="Dataset Detail Page"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Click on any dataset card or table row to view detailed information including complete metadata, related files, edit history, and filepath information.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm">
                      <strong>⚠️ Important:</strong> Use the filepath shown to access files via Data Request Management System or User-based Dashboard.
                    </p>
                  </div>
                </section>

                {/* Edit History */}
                <section className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <Clock className="h-8 w-8 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">Edit History</h2>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Track all changes made to a dataset with complete audit trail showing who made the change, when it was made, what field was changed, and the before/after values. Changes can be made via web interface or Excel upload.
                  </p>
                  <div className="flex justify-center">
                    <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md w-1/2">
                      <img
                        src={editHistoryImage}
                        alt="Edit History"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                </section>

                {/* Update Panel */}
                <section className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <FileText className="h-8 w-8 text-purple-600" />
                    <h2 className="text-2xl font-bold text-gray-900">Update Panel (with User Authorization)</h2>
                  </div>
                  <div className="rounded-lg overflow-hidden border-2 border-gray-200 shadow-md mb-4">
                    <img
                      src={updatePanelImage}
                      alt="Update Panel"
                      className="w-full h-auto"
                    />
                  </div>
                  <p className="text-gray-600">
                    The Update Panel allows authorized users to manage datasets, upload Excel files, and configure system settings. Features include adding records manually, batch updates via Excel, downloading all data, downloading templates, and managing system entities (Data Types, Data Sources, Medications).
                  </p>
                </section>
              </>
            )}


          </div>

          {/* Right Column - Login Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-8 sticky top-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Ready to Get Started?
                </h2>
                <p className="text-gray-600 text-sm">
                  Sign in to access the system
                </p>
              </div>


              {/* Login Button */}
              <button
                onClick={handleLogin}
                disabled={isLoading}
                className={`group relative w-full flex justify-center items-center py-4 px-6 border border-transparent text-base font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl ${
                  activeGuide === 'publication'
                    ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:ring-green-500'
                    : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 focus:ring-purple-500'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Redirecting...
                  </div>
                ) : (
                  <>
                    <Mail className="h-5 w-5 mr-2" />
                    Sign in Here
                    <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              {/* Account Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Need an Account?</h3>
                <div className="text-xs text-gray-600 space-y-2">
                  <div className="flex items-start">
                    <CheckCircle2 className={`h-4 w-4 mr-2 mt-0.5 flex-shrink-0 ${activeGuide === 'publication' ? 'text-green-600' : 'text-purple-600'}`} />
                    <p>Contact Griffith Gao to create an account</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className={`h-4 w-4 mr-2 mt-0.5 flex-shrink-0 ${activeGuide === 'publication' ? 'text-green-600' : 'text-purple-600'}`} />
                    <p>Use the email and password provided</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className={`h-4 w-4 mr-2 mt-0.5 flex-shrink-0 ${activeGuide === 'publication' ? 'text-green-600' : 'text-purple-600'}`} />
                    <p>Click "Forgot password" to change your password</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-600">
            Publication Committee & Tracking System {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
